import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Users, Armchair, Wifi, Droplets, Snowflake, ArrowRight } from 'lucide-react';

const FleetSection = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleBook = (service) => {
    navigate(`/booking?service=${service}`);
  };

  return (
    <section id="fleet" className="py-10 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-10">
          <p className="text-[#D4AF37] text-sm tracking-[4px] uppercase mb-4">
            Premium Vehicles
          </p>
          <h2 className="text-4xl md:text-5xl font-serif text-white mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('fleet_title')}
          </h2>
          <p className="text-[#A1A1AA] max-w-2xl mx-auto">
            {t('fleet_subtitle')}
          </p>
        </div>

        {/* Two Main Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          
          {/* Business Class Card */}
          <div className="bg-[#0d0d0d] border border-[#D4AF37]/20 overflow-hidden group hover:border-[#D4AF37]/50 transition-all duration-500 flex flex-col" data-testid="fleet-business-card">
            {/* Image - clean car photo */}
            <div className="relative overflow-hidden h-[350px]">
              <img
                src="https://customer-assets.emergentagent.com/job_dumitru-transport/artifacts/3g45zgdk_446de9a1-4e15-4b83-b77f-96cbab8b3982.jpeg"
                alt="Business Class Fleet"
                className="w-full h-full object-contain bg-[#0d0d0d]"
              />
            </div>
            
            {/* Content */}
            <div className="p-8">
              <h3 className="text-3xl font-serif text-[#D4AF37] tracking-wider mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                BUSINESS CLASS
              </h3>
              <p className="text-[#D4AF37]/70 text-xs tracking-[3px] uppercase mb-6">
                V-CLASS &bull; S-CLASS &bull; AUDI A8 &bull; BMW 7 SERIES
              </p>
              
              <div className="w-16 h-[1px] bg-[#D4AF37]/40 mb-6"></div>
              
              <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">
                {t('fleet_business_desc1') || 'Travel at the highest level of comfort, elegance, and refinement. Our selection of Business Class vehicles is dedicated to clients who appreciate discretion, generous space, performance, and the latest generation technology.'}
              </p>
              <p className="text-[#A1A1AA] text-sm leading-relaxed mb-8">
                {t('fleet_business_desc2') || "Whether it's for a business trip, an important event, or a special transfer, we offer you a premium experience, adapted to the most demanding standards."}
              </p>
              
              {/* Features Icons */}
              <div className="grid grid-cols-4 gap-4 mb-8">
                <div className="text-center">
                  <Users className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                  <p className="text-white text-[10px] tracking-wider uppercase">3-7</p>
                  <p className="text-white text-[10px] tracking-wider uppercase">{t('fleet_passengers') || 'Passengers'}</p>
                </div>
                <div className="text-center">
                  <Armchair className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                  <p className="text-white text-[10px] tracking-wider uppercase">{t('fleet_leather') || 'Leather'}</p>
                  <p className="text-white text-[10px] tracking-wider uppercase">Premium</p>
                </div>
                <div className="text-center">
                  <Wifi className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                  <p className="text-white text-[10px] tracking-wider uppercase">WiFi</p>
                  <p className="text-white text-[10px] tracking-wider uppercase">{t('fleet_free') || 'Free'}</p>
                </div>
                <div className="text-center">
                  <Droplets className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                  <p className="text-white text-[10px] tracking-wider uppercase">{t('fleet_water') || 'Water'}</p>
                  <p className="text-white text-[10px] tracking-wider uppercase">{t('fleet_included') || 'Included'}</p>
                </div>
              </div>
              
              {/* Book Now Button */}
              <button
                onClick={() => handleBook('business')}
                className="w-full flex items-center justify-between bg-[#D4AF37] text-black px-6 py-4 font-medium tracking-wider uppercase hover:bg-[#D4AF37]/90 transition-colors"
                data-testid="fleet-business-book"
              >
                <span>Book Now</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Premium Limousine Card */}
          <div className="bg-[#0d0d0d] border border-[#D4AF37]/20 overflow-hidden group hover:border-[#D4AF37]/50 transition-all duration-500 flex flex-col" data-testid="fleet-limousine-card">
            {/* Image - clean car photo */}
            <div className="relative overflow-hidden h-[350px]">
              <img
                src="https://customer-assets.emergentagent.com/job_dumitru-transport/artifacts/55ac8bl1_IMG_9226.jpeg"
                alt="Premium Limousine"
                className="w-full h-full object-contain bg-[#0d0d0d]"
              />
            </div>
            
            {/* Content */}
            <div className="p-8">
              <h3 className="text-3xl font-serif text-[#D4AF37] tracking-wider mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                LIMUZINE
              </h3>
              <p className="text-[#D4AF37]/70 text-xs tracking-[3px] uppercase mb-6">
                MERCEDES S-CLASS &bull; MERCEDES E-CLASS &bull; AUDI A6 &bull; BMW 5 SERIES
              </p>
              
              <div className="w-16 h-[1px] bg-[#D4AF37]/40 mb-6"></div>
              
              <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">
                {t('fleet_limo_desc1') || 'Our limousines offer the perfect balance between elegance, comfort, and performance. Ideal for individual transfers, business meetings, or special events, these vehicles impress with their design, refinement, and a relaxing travel experience.'}
              </p>
              <p className="text-[#A1A1AA] text-sm leading-relaxed mb-8">
                {t('fleet_limo_desc2') || 'Every detail is carefully crafted to offer you safety, discretion, and comfort, regardless of the destination.'}
              </p>
              
              {/* Features Icons */}
              <div className="grid grid-cols-4 gap-4 mb-8">
                <div className="text-center">
                  <Users className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                  <p className="text-white text-[10px] tracking-wider uppercase">{t('fleet_upto') || 'Up to'} 3</p>
                  <p className="text-white text-[10px] tracking-wider uppercase">{t('fleet_passengers') || 'Passengers'}</p>
                </div>
                <div className="text-center">
                  <Armchair className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                  <p className="text-white text-[10px] tracking-wider uppercase">{t('fleet_leather') || 'Leather'}</p>
                  <p className="text-white text-[10px] tracking-wider uppercase">Luxury</p>
                </div>
                <div className="text-center">
                  <Snowflake className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                  <p className="text-white text-[10px] tracking-wider uppercase">{t('fleet_climate') || 'Climate'}</p>
                  <p className="text-white text-[10px] tracking-wider uppercase">{t('fleet_individual') || 'Individual'}</p>
                </div>
                <div className="text-center">
                  <Droplets className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                  <p className="text-white text-[10px] tracking-wider uppercase">{t('fleet_water') || 'Water'}</p>
                  <p className="text-white text-[10px] tracking-wider uppercase">{t('fleet_included') || 'Included'}</p>
                </div>
              </div>
              
              {/* Book Now Button */}
              <button
                onClick={() => handleBook('standard_premium')}
                className="w-full flex items-center justify-between bg-[#D4AF37] text-black px-6 py-4 font-medium tracking-wider uppercase hover:bg-[#D4AF37]/90 transition-colors"
                data-testid="fleet-limousine-book"
              >
                <span>Book Now</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Sprinter Card - Full Width */}
        <div className="bg-[#0d0d0d] border border-[#D4AF37]/20 overflow-hidden hover:border-[#D4AF37]/50 transition-all duration-500" data-testid="fleet-sprinter-card">
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="relative overflow-hidden md:min-h-full">
              <img
                src="https://customer-assets.emergentagent.com/job_dumitru-transport/artifacts/osepj5qt_b2632dbe-3169-4062-b387-6ebb5f4c79e8.jpeg"
                alt="Mercedes Sprinter"
                className="w-full h-full object-cover"
                style={{ minHeight: '380px' }}
              />
              <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 bg-black/60 backdrop-blur-sm border border-white/10">
                <Users className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-sm text-white">8 Transfer</span>
              </div>
            </div>
            <div className="p-8 flex flex-col justify-center">
              <h3 className="text-3xl font-serif text-[#D4AF37] tracking-wider mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                SPRINTER
              </h3>
              <p className="text-[#D4AF37]/70 text-xs tracking-[3px] uppercase mb-4">
                MERCEDES SPRINTER &bull; PREMIUM INTERIOR &bull; GROUP TRAVEL
              </p>
              <div className="w-16 h-[1px] bg-[#D4AF37]/40 mb-4"></div>
              <p className="text-[#A1A1AA] text-sm leading-relaxed mb-6">
                {t('fleet_sprinter_desc_long') || 'Perfect for group travel up to 8 passengers. Our Mercedes Sprinter offers spacious comfort with premium interior, ideal for airport transfers, corporate events, and group tours.'}
              </p>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center">
                  <Users className="w-5 h-5 text-[#D4AF37] mx-auto mb-1" />
                  <p className="text-white text-[10px] tracking-wider uppercase">{t('fleet_upto') || 'Up to'} 8</p>
                  <p className="text-white text-[10px] tracking-wider uppercase">{t('fleet_passengers') || 'Passengers'}</p>
                </div>
                <div className="text-center">
                  <Wifi className="w-5 h-5 text-[#D4AF37] mx-auto mb-1" />
                  <p className="text-white text-[10px] tracking-wider uppercase">WiFi</p>
                  <p className="text-white text-[10px] tracking-wider uppercase">{t('fleet_free') || 'Free'}</p>
                </div>
                <div className="text-center">
                  <Droplets className="w-5 h-5 text-[#D4AF37] mx-auto mb-1" />
                  <p className="text-white text-[10px] tracking-wider uppercase">{t('fleet_water') || 'Water'}</p>
                  <p className="text-white text-[10px] tracking-wider uppercase">{t('fleet_included') || 'Included'}</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/booking')}
                className="w-full flex items-center justify-between border border-[#D4AF37] text-[#D4AF37] px-6 py-4 font-medium tracking-wider uppercase hover:bg-[#D4AF37] hover:text-black transition-colors"
                data-testid="fleet-sprinter-book"
              >
                <span>Book Now</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FleetSection;
